/**
 * Portfolio analytics mock — Story UI-U7.
 *
 * Shaped EXACTLY like PortfolioStatisticsDto so wiring GET /statistics/portfolio is a
 * drop-in swap (the useHeuteRenewals / V10-J mock-swap pattern). Synthetic Swiss-broker
 * data — no real PII, no secrets.
 *
 * IMPORTANT: the default fixture OMITS the optional future-insights fields
 * (gaps_count / ki_beobachtungen) so mock mode mirrors live behaviour (the api never
 * emits them today). MOCK_PORTFOLIO_STATISTICS_WITH_INSIGHTS is a SECOND fixture that
 * DOES carry them — used ONLY by the feature-detect test to prove the UI lights up when
 * a future backend supplies them.
 *
 * [Source: apps/web/src/features/renewals/mock.ts] (wire-shaped mock precedent)
 * [Source: docs/stories/ui-u7-portfolio.md Task 5 + Dev Notes]
 */

import type { PortfolioStatisticsDto, PraemienMonatDto } from '@brokly/shared';

// 12 month-ends, oldest first — a gently rising in-force premium + policy count.
const SERIES: PraemienMonatDto[] = [
  { month: '2025-08', praemie_chf: '1042000.00', policen: 118 },
  { month: '2025-09', praemie_chf: '1055500.00', policen: 121 },
  { month: '2025-10', praemie_chf: '1061000.00', policen: 123 },
  { month: '2025-11', praemie_chf: '1078900.00', policen: 126 },
  { month: '2025-12', praemie_chf: '1090400.00', policen: 128 },
  { month: '2026-01', praemie_chf: '1112600.00', policen: 131 },
  { month: '2026-02', praemie_chf: '1128300.00', policen: 133 },
  { month: '2026-03', praemie_chf: '1140900.00', policen: 135 },
  { month: '2026-04', praemie_chf: '1163200.00', policen: 138 },
  { month: '2026-05', praemie_chf: '1181700.00', policen: 141 },
  { month: '2026-06', praemie_chf: '1204800.00', policen: 144 },
  { month: '2026-07', praemie_chf: '1226500.00', policen: 147 },
];

export const MOCK_PORTFOLIO_STATISTICS: PortfolioStatisticsDto = {
  as_of: '2026-07-28T07:30:00.000Z',
  kpis: {
    jahrespraemie_chf: '1226500.00',
    // (1226500 - 1042000) / 1042000 * 100 ≈ 17.7
    jahrespraemie_delta_pct: 17.7,
    courtage_ytd_chf: '148200.00',
    aktive_policen: 147,
    aktive_policen_delta_30d: 4,
    kunden: 86,
    kunden_delta_30d: 3,
    versicherer: 12,
    versicherer_delta_30d: 1,
  },
  stats: {
    automatisierungsgrad_pct: 78,
    offene_aufgaben: 12,
    offene_nachrichten: 5,
    rechnungen_in_klaerung: 3,
    ausschreibungen_offen: 6,
    ausschreibungen_erledigt: 9,
  },
  praemien_entwicklung: SERIES,
  sparten: [
    { sparte: 'sach', policen: 41, praemie_chf: '392000.00' },
    { sparte: 'haftpflicht', policen: 33, praemie_chf: '288500.00' },
    { sparte: 'uvg', policen: 28, praemie_chf: '241900.00' },
    { sparte: 'ktg', policen: 21, praemie_chf: '176300.00' },
    { sparte: 'leben', policen: 14, praemie_chf: '92800.00' },
    { sparte: null, policen: 10, praemie_chf: '35000.00' },
  ],
  top_kunden: [
    {
      customer_state_id: 'cs-basler',
      subject_type: 'firma',
      subject_id: 'k-basler',
      name: 'Basler Chemie AG',
      policen: 9,
      praemie_chf: '248200.00',
    },
    {
      customer_state_id: 'cs-geneve',
      subject_type: 'firma',
      subject_id: 'k-geneve',
      name: 'Genève Maritime SA',
      policen: 7,
      praemie_chf: '193900.00',
    },
    {
      customer_state_id: 'cs-bergland',
      subject_type: 'firma',
      subject_id: 'k-bergland',
      name: 'Bergland Logistik AG',
      policen: 6,
      praemie_chf: '164400.00',
    },
    {
      customer_state_id: 'cs-alpina',
      subject_type: 'firma',
      subject_id: 'k-alpina',
      name: 'Alpina Pharma AG',
      policen: 5,
      praemie_chf: '131500.00',
    },
    {
      customer_state_id: 'cs-zpaeg',
      subject_type: 'firma',
      subject_id: 'k-zpaeg',
      name: 'Zürcher Präzision AG',
      policen: 4,
      praemie_chf: '97400.00',
    },
  ],
  letzte_aktivitaeten: [
    {
      id: 'act-1',
      type: 'note',
      content: 'Offertanfrage Betriebshaftpflicht dokumentiert',
      actor_kind: 'human',
      actor_name: 'Lara Meier',
      subject_name: 'Basler Chemie AG',
      task_id: null,
      created_at: '2026-07-28T06:55:00.000Z',
    },
    {
      id: 'act-2',
      type: 'system',
      content: 'Prämienrechnung automatisch geprüft (K0)',
      actor_kind: 'system',
      actor_name: null,
      subject_name: 'Genève Maritime SA',
      task_id: null,
      created_at: '2026-07-28T06:20:00.000Z',
    },
    {
      id: 'act-3',
      type: 'call',
      content: 'Rückruf wegen Vertragsverlängerung',
      actor_kind: 'human',
      actor_name: 'Jonas Weber',
      subject_name: 'Bergland Logistik AG',
      task_id: 'task-9001',
      created_at: '2026-07-27T15:10:00.000Z',
    },
    {
      id: 'act-4',
      type: 'system',
      content: 'Courtageabrechnung eingegangen und abgeglichen',
      actor_kind: 'system',
      actor_name: null,
      subject_name: null,
      task_id: null,
      created_at: '2026-07-27T11:42:00.000Z',
    },
    {
      id: 'act-5',
      type: 'note',
      content: 'Schadenmeldung erfasst und weitergeleitet',
      actor_kind: 'human',
      actor_name: 'Lara Meier',
      subject_name: 'Alpina Pharma AG',
      task_id: 'task-9002',
      created_at: '2026-07-27T09:05:00.000Z',
    },
    {
      id: 'act-6',
      type: 'meeting',
      content: 'Jahresgespräch protokolliert',
      actor_kind: 'human',
      actor_name: 'Jonas Weber',
      subject_name: 'Zürcher Präzision AG',
      task_id: null,
      created_at: '2026-07-26T14:30:00.000Z',
    },
  ],
  // gaps_count / ki_beobachtungen: intentionally ABSENT (mirror the live api).
};

/**
 * The future-insights variant — carries gaps_count + ki_beobachtungen so the
 * feature-detect test can prove the GAPs tile + KI-Beobachtungen card light up
 * when (a later) backend supplies them. NOT used by the default hook.
 */
export const MOCK_PORTFOLIO_STATISTICS_WITH_INSIGHTS: PortfolioStatisticsDto = {
  ...MOCK_PORTFOLIO_STATISTICS,
  gaps_count: 7,
  ki_beobachtungen: [
    {
      id: 'ki-1',
      titel: 'Unterdeckung Sachversicherung',
      text: 'Bei 3 Kunden liegt die versicherte Summe unter dem geschätzten Ersatzwert.',
      tone: 'risiko',
      konfidenz: 82,
    },
    {
      id: 'ki-2',
      titel: 'Cross-Selling-Potenzial UVG',
      text: '5 Firmenkunden ohne Krankentaggeld-Deckung mit passendem Profil.',
      tone: 'chance',
      konfidenz: 74,
    },
  ],
};
